import { CommonModule } from '@angular/common';
import { Component, Host, Inject, Input, NgModule, OnInit, Optional } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { CUSTOMERENVIRONMENT, WINDOW } from '@app/common/app-tokens';
import { Utilities } from '@app/common/utilities';
import { BreakPoints } from '@app/common/breakpoints';
import { ErrorTemplatesModule } from '@app/components/error-templates/error-templates.component';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { HideParentIfEmptyDirective } from '@app/directives/hide-parent-if-empty.directive';
import { SearchResult } from '@app/models/search-result';
import { LinkModule } from '@app/directives/link.directive';
import { SafeModule } from '@app/pipes/safe.pipe';
import { SearchService } from '@app/services/search.service';
import { UserService } from '@app/services/user.service';
import { Environment } from '@environments/interface.environment';
import { formatISO } from 'date-fns';
import { Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, map, startWith, tap } from 'rxjs/operators';

@Component({
  selector: 'app-birthdays',
  templateUrl: './birthdays.component.html',
  styleUrls: ['./birthdays.component.scss']
})

export class BirthdaysComponent implements OnInit {

  // Amount of birthdays to display on each row
  @Input() useTeamsCongratulations = true;

  amountOfBirthdaysToDisplay: number;
  amountOfBirthdaysToIncreaseWith: number;
  amountOfBirthdaysToDisplayCurrently: number;

  loading = false;
  birthdayPersonsToday$: Observable<Array<SearchResult>>;
  today = new Date();
  errorSubject$ = new Subject();
  moreBirthdaysSubject = new Subject<number>();
  fetchedAmount = 0;
  dateToQuery: string;
  componentName = 'Your birthday';

  constructor(private searchService: SearchService,
              private userService: UserService,
              @Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              @Host() @Optional() private hideParentIfEmptyDirective: HideParentIfEmptyDirective,
              @Inject(WINDOW) private window: Window) { }

  ngOnInit() {

    // Set querydate to ask for year 2000 since the property SPS-Birthday always stores the birthday date with year 2000.
    // The date format for queries should be in this format 2000-MM-dd (ISO8601).
    // For development purpose we set this to 21 november becasue we know we'll get some values then.
    const date = this.environment.production ? new Date(2000, new Date().getMonth(), new Date().getDate()) : new Date(2000, 11, 17);
    this.dateToQuery = formatISO(date, { representation: 'date' });

    // Set amount to load dynamically based on screen size
    const breakPoint = Utilities.getCurrentBootstrapBreakpoint(this.window);
    this.amountOfBirthdaysToDisplay = 4;
    if (breakPoint === BreakPoints.xxxl || breakPoint === BreakPoints.xxl) {
      this.amountOfBirthdaysToDisplay = 6;
    }
    if (breakPoint === BreakPoints.xs) {
      this.amountOfBirthdaysToDisplay = 3;
    }
    this.amountOfBirthdaysToIncreaseWith = this.amountOfBirthdaysToDisplay;
    this.amountOfBirthdaysToDisplayCurrently = this.amountOfBirthdaysToDisplay;

    this.birthdayPersonsToday$ = this.moreBirthdaysSubject
      .pipe(
        startWith(this.amountOfBirthdaysToDisplayCurrently + 1),
        concatMap((amount) => {
          this.loading = true;
          return this.searchService.getPeopleWithBirthdayDate(this.dateToQuery, amount);
        }),
        map((results) => {
          this.fetchedAmount = results.length;

          if (results.length === 0) {
            this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.hideElement();
          } else {
            this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.showElement();
          }

          if (this.fetchedAmount > this.amountOfBirthdaysToDisplayCurrently) {
            results = results.slice(0, this.amountOfBirthdaysToDisplayCurrently);
          }
          return results.map(result => {
            if (result.WorkEmail) {
              result.photoUrl$ = this.userService.getUserPhotoByUpn(result.WorkEmail);
            }
            return result;
          });
        }),
        tap(() => {
          this.loading = false;
        }),
        catchError(err => {
          this.errorSubject$.next(err);
          return of(null);
        }));
  }

  getPreferredNameLetters(preferredName: string) {
    return Utilities.generateInitials(preferredName);
  }

  showMoreBirthdays() {
    this.amountOfBirthdaysToDisplayCurrently = this.amountOfBirthdaysToDisplayCurrently + this.amountOfBirthdaysToIncreaseWith;
    this.moreBirthdaysSubject.next(this.amountOfBirthdaysToDisplayCurrently + 1);
  }

  showLessBirthdays() {
    this.amountOfBirthdaysToDisplayCurrently = this.amountOfBirthdaysToDisplay;
    this.moreBirthdaysSubject.next(this.amountOfBirthdaysToDisplayCurrently + 1);
  }
}

@NgModule({
  declarations: [BirthdaysComponent],
  exports: [BirthdaysComponent],
  imports: [
    CommonModule,
    WidgetPlaceholderModule,
    LinkModule,
    MatButtonModule,
    ErrorTemplatesModule,
    SafeModule
  ],
})

export class BirthdaysModule { }
